import { useCallback, useEffect, useRef, useState } from "react";

import { type DocumentSnapshot, isOfficeDocumentAvailable, readDocumentSnapshot } from "./snapshot";

/** Selection changes arrive on every caret move; read the document at most this often. */
const SELECTION_DEBOUNCE_MS = 400;

/**
 * Keep a current snapshot of the document.
 *
 * The runtime reads the run context synchronously at send time, and Word can
 * only be read asynchronously, so the snapshot is held in a ref: refreshed
 * (debounced) whenever the selection changes, and refreshed once more, awaited,
 * right before each send so the agent sees the document as it is at that
 * moment. The selected text is also exposed as state, for the UI that reacts
 * to it (the selection action chips).
 * @returns A synchronous getter, an awaitable refresh and the current selection.
 */
export const useDocumentSnapshot = () => {
  const snapshotRef = useRef<DocumentSnapshot | null>(null);
  const inFlightRef = useRef<Promise<DocumentSnapshot | null> | null>(null);
  const [selectedText, setSelectedText] = useState<string | null>(null);

  // Question ranges cost one Word object per paragraph, so they are read
  // only on a full refresh (before a send) and carried over otherwise.
  const questionsRef = useRef<DocumentSnapshot["questions"]>([]);
  const inFlightFullRef = useRef(false);

  const refresh = useCallback(async (options: { full?: boolean } = {}): Promise<DocumentSnapshot | null> => {
    if (inFlightRef.current) {
      // A light read is running; a full one waits for it, then reads again.
      if (!options.full || inFlightFullRef.current) return inFlightRef.current;
      await inFlightRef.current.catch(() => null);
    }
    inFlightFullRef.current = Boolean(options.full);
    const pending = readDocumentSnapshot({ questions: options.full })
      .then((snapshot) => {
        if (snapshot) {
          if (options.full) questionsRef.current = snapshot.questions;
          snapshotRef.current = options.full ? snapshot : { ...snapshot, questions: questionsRef.current };
          setSelectedText(snapshot.selection?.text ?? null);
        }
        return snapshot;
      })
      .catch((error) => {
        console.warn("Could not read the document", error);
        return snapshotRef.current;
      })
      .finally(() => {
        inFlightRef.current = null;
        inFlightFullRef.current = false;
      });
    inFlightRef.current = pending;
    return pending;
  }, []);

  useEffect(() => {
    if (!isOfficeDocumentAvailable()) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const onSelectionChanged = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => void refresh(), SELECTION_DEBOUNCE_MS);
    };
    void refresh();
    Office.context.document.addHandlerAsync(Office.EventType.DocumentSelectionChanged, onSelectionChanged);
    return () => {
      if (timer) clearTimeout(timer);
      Office.context.document.removeHandlerAsync(Office.EventType.DocumentSelectionChanged, {
        handler: onSelectionChanged,
      });
    };
  }, [refresh]);

  const getSnapshot = useCallback(() => snapshotRef.current, []);

  return { getSnapshot, refresh, selectedText };
};
